import { Grid } from '@mui/material';
import { Button, Divider, Spacer } from 'components/atoms';
import TransactionInProgressModal from 'components/atoms/a-transaction-in-progress-modal';
import { NewUseUpdatedCart, useUpdatedCart } from 'components/molecules/Cart/hooks';
import MadinaPointsInput from 'components/molecules/m-madina-points-input';
import CheckoutDetails from 'components/organisms/o-checkout-details';
import SEARCH_PARAMS from 'constants/searchParams';
import renderIcon from 'icon';
import { enqueueSnackbar } from 'notistack';
import React, { useRef } from 'react';
import { IoArrowBackCircleSharp } from 'react-icons/io5';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { buyNowPayLaterStepsAtom } from 'store/atoms/buyNowPayLaterAtom';
import { cartListAtom } from 'store/atoms/cartAtom';
import { checkoutDetailsAtom } from 'store/atoms/checkoutAtom';
import checkoutVariablesAtom from 'store/atoms/checkoutVariables';
import modalAtom from 'store/atoms/modals';
import { userIsLoggedIn, userRoleAtom } from 'store/atoms/userAtom';
import { LoanSummary, PaymentModal, UsePointsModal } from './component';
import { BuyNowPayLater } from './component/BuyNowPayLater';
import { RejectPaymentModal } from './component/RejectPaymentModal';
import usePayment from './hooks';
import { CheckoutContainer, MainGridCon, MobileHeading } from './style';

const CheckoutPage = () => {
  useUpdatedCart();
  const go = useNavigate();
  const { pathname } = useLocation();

  const { orderId } = useParams();
  const cartList = useRecoilValue(cartListAtom);
  const userRole = useRecoilValue(userRoleAtom);
  const isLogged = useRecoilValue(userIsLoggedIn);
  const [isPoint, setIsPoint] = React.useState(false);
  const modalState = useRecoilValue(modalAtom);
  const [businessPayOpen, setBusinessPayOpen] = React.useState(false);
  const buyNowPayLaterStep = useRecoilValue(buyNowPayLaterStepsAtom);
  const [checkoutDetails, setCheckoutDetails] = useRecoilState(checkoutDetailsAtom);
  const [checkoutVariables, setCheckoutVariables] = useRecoilState(checkoutVariablesAtom);
  const [isSubmitting, setIsSubmitting] = React.useState(false); // Add a state to track submission
  const submissionInProgress = useRef(false);
  const lastSubmissionTime = useRef(0);
  const SUBMISSION_COOLDOWN = 2000;

  const {
    onWalletSuccess,
    initiatePayment,
    onClose: paymentClose,
    onSuccess: checkoutSuccess,
    initiateBusinessPostPayment
  } = usePayment();

  const handleNavigateToSignin: any = (): void => {
    go(`/login?${SEARCH_PARAMS.LOGIN_SUCCESS_REROUTE}=${pathname}`, {
      state: { from: pathname }
    });
  };

  const serverCart = NewUseUpdatedCart()?.data;
  const cartOrder = (serverCart?.orders as any[])?.find(order => order?.locationId === Number(orderId));

  React.useEffect(() => {
    if (checkoutDetails.deliveryMethod === 'homedelivery' && checkoutVariables.selectedCarrier?.total !== undefined) {
      setCheckoutDetails(prev => ({
        ...prev,
        shippingFee: checkoutVariables.selectedCarrier?.total ?? 0
      }));
    } else {
      setCheckoutDetails(prev => ({ ...prev, shippingFee: 0 }));
    }
  }, [checkoutDetails.deliveryMethod, checkoutVariables.selectedCarrier, setCheckoutDetails]);

  React.useEffect(() => {
    if (!orderId) {
      go('/');
    }
  }, [cartList, go, orderId]);

  return (
    <div>
      <Spacer height={10} />
      <div className="flex items-center gap-2 text-sm text-gray-500 ml-3 mb-2">
        {renderIcon(IoArrowBackCircleSharp, {
          size: 24,
          className: 'text-black flex-shrink-0 cursor-pointer hover:text-gray-600 transition-colors',
          onClick: () => go(-1)
        })}
        <span>
          {cartOrder?.vendorName
            ? cartOrder.vendorName.charAt(0).toUpperCase() + cartOrder.vendorName.slice(1).toLowerCase()
            : 'Madina'}
        </span>
        <span>›</span>
        <span>Checkout</span>
      </div>
      {/* <h3>Checkout</h3> */}
      {buyNowPayLaterStep === 4 ? (
        <LoanSummary />
      ) : (
        <>
          {/* Mobile heading at top of page */}
          <MobileHeading>
            <h2>Checkout</h2>
            <p className="top">Review the address, payment method and tips 😉 to complete your purchase</p>
            <Spacer height={10} />
            <Divider />
          </MobileHeading>

          <MainGridCon container spacing={{ xs: 0, sm: 2 }}>
            <Grid item xs={12} md={7.25} lg={8}>
              <CheckoutContainer>
                {/* Desktop heading inside container */}
                <h2 className="desktop-heading">Checkout</h2>

                {/* <p className="top">Review the address, payment method and tips 😉 to complete your purchase</p> */}
                <CheckoutDetails.Delivery />
                {/* <CheckoutDetails.Contact /> */}
                <CheckoutDetails.Logistics />

                <MadinaPointsInput
                  onChange={points =>
                    setCheckoutVariables(curr => ({
                      ...curr,
                      pointsUsed: points
                    }))
                  }
                />
                <Spacer height={10} />
                {/* <RecurringDelivery /> */}
                <Button
                  isLoading={checkoutVariables.checkoutLoading || isSubmitting}
                  extraStyles={{ fontSize: '17px' }}
                  onClick={async () => {
                    // Prevent rapid successive clicks
                    if (submissionInProgress.current) {
                      enqueueSnackbar('Payment is already being processed, please wait...', { variant: 'info' });
                      return;
                    }

                    const now = Date.now();
                    if (now - lastSubmissionTime.current < SUBMISSION_COOLDOWN) {
                      enqueueSnackbar('Please wait a moment before trying again', { variant: 'warning' });
                      return;
                    }

                    submissionInProgress.current = true;
                    lastSubmissionTime.current = now;
                    setIsSubmitting(true);

                    try {
                      if (isLogged) {
                        // Your existing logic here...
                        if (userRole === 'BUSINESS') {
                          // ... existing business logic
                          setBusinessPayOpen(true);
                        } else {
                          await initiatePayment();
                        }
                      } else {
                        handleNavigateToSignin();
                      }
                    } finally {
                      setTimeout(() => {
                        submissionInProgress.current = false;
                        setIsSubmitting(false);
                      }, 1000);
                    }
                  }}
                  disabled={isSubmitting} // Disable button while submitting
                >
                  Make Payment
                </Button>

                <Spacer height={20} />
              </CheckoutContainer>
            </Grid>

            <CheckoutDetails.Summary />
          </MainGridCon>
        </>
      )}
      <CheckoutDetails.BusinessPaymentModal
        opened={businessPayOpen}
        initiatePayment={initiatePayment}
        onClose={() => setBusinessPayOpen(false)}
        initiateBusinessPostPayment={initiateBusinessPostPayment}
      />

      {/* <Spacer height={100} /> */}

      {modalState.paymentModalOpen && (
        <PaymentModal
          onWalletSuccess={onWalletSuccess}
          onSuccess={checkoutSuccess}
          onClose={paymentClose}
          amount={checkoutDetails.grandTotal}
        />
      )}

      {isPoint && <UsePointsModal showPaymentModal={initiatePayment} setIsPoint={setIsPoint} />}

      {modalState.paymentFailureModalOpen && <RejectPaymentModal />}
      <BuyNowPayLater />
      <TransactionInProgressModal />
    </div>
  );
};

export default CheckoutPage;
